import { styled } from '@mui/material/styles'
import Box from '@/components/common/Box'
import { colors, typography, spacing, radius, sizing, focusVisible } from '@/styles/designTokens'

export const ItemCard = styled(Box)({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'flex-start',
	gap: spacing.sm,
	padding: spacing.xl,
	cursor: 'pointer',
	'&:hover': {
		backgroundColor: colors.overlaySubtle,
	},
	'&:focus-visible': {
		outline: `${focusVisible.outline} ${colors.brand}`,
		outlineOffset: focusVisible.outlineOffset,
	},
})

export const ItemImage = styled('img')({
	width: sizing.cartItemImage,
	height: sizing.cartItemImage,
	borderRadius: radius.card,
	objectFit: 'cover',
	flexShrink: 0,
})

export const ItemImagePlaceholder = styled(Box)({
	width: sizing.cartItemImage,
	height: sizing.cartItemImage,
	borderRadius: radius.card,
	backgroundColor: colors.overlayLight,
	flexShrink: 0,
})

export const ItemDetails = styled(Box)({
	flex: '1 1 auto',
	minWidth: 0,
	display: 'flex',
	flexDirection: 'column',
	gap: spacing.md,
})

export const ItemNameGroup = styled(Box)({
	display: 'flex',
	flexDirection: 'column',
	gap: spacing.xs,
})

export const ItemName = styled('span')({
	fontSize: typography.fontSizeMd,
	fontWeight: typography.fontWeightBold,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	color: colors.textPrimary,
})

export const ItemModifiers = styled('span')({
	fontSize: typography.fontSizeSm,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightSm,
	letterSpacing: typography.letterSpacingWidest,
	color: colors.textSecondary,
})

export const ItemPrice = styled('span')({
	fontSize: typography.fontSizeMd,
	fontWeight: typography.fontWeightBold,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	color: colors.textPrimary,
})

export const ItemActions = styled(Box)({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	gap: spacing.xs,
	flexShrink: 0,
})

const PILL_COLLAPSED = sizing.iconButton // 40
const PILL_EXPANDED = 120 // 3× button width
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)'
const DURATION_EXPAND = '240ms'
const DURATION_LEAD = '160ms'

export const QTY_EXPAND_SHELL_CLASS = 'QtyExpandShell'
export const QTY_EXPAND_TRACK_CLASS = 'QtyExpandTrack'
export const QTY_EXPAND_BOX_CLASS = 'QtyExpandBox'
export const QTY_EXPAND_TRASH_OUTSIDE_CLASS = 'QtyExpandTrashOutside'

const BaseButton = styled('button')({
	fontFamily: 'inherit',
	background: 'none',
	border: 'none',
	padding: 0,
	cursor: 'pointer',
})

export const QtyExpand = styled(Box)({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	gap: spacing.xs,
	height: PILL_COLLAPSED,

	'@media (prefers-reduced-motion: reduce)': {
		[`& .${QTY_EXPAND_SHELL_CLASS}`]: { transition: 'none' },
		[`& .${QTY_EXPAND_TRACK_CLASS}`]: { transition: 'none' },
		[`& .${QTY_EXPAND_BOX_CLASS}`]: { transition: 'none' },
		[`& .${QTY_EXPAND_TRASH_OUTSIDE_CLASS}`]: { transition: 'none' },
	},

	'&:hover, &:focus-within': {
		gap: 0,

		[`& .${QTY_EXPAND_TRASH_OUTSIDE_CLASS}`]: {
			opacity: 0,
			width: 0,
			pointerEvents: 'none',
		},
		[`& .${QTY_EXPAND_SHELL_CLASS}`]: {
			width: PILL_EXPANDED,
			boxShadow: `inset 0 0 0 1px ${colors.border}`,
			borderRadius: radius.badge,
		},
		[`& .${QTY_EXPAND_TRACK_CLASS}`]: {
			transform: 'translate3d(0, 0, 0)',
		},
		[`& .${QTY_EXPAND_BOX_CLASS}`]: {
			boxShadow: 'none',
			borderRadius: 0,
		},
	},
})

export const QtyExpandTrashOutside = styled(BaseButton)({
	flexShrink: 0,
	width: PILL_COLLAPSED,
	height: PILL_COLLAPSED,
	display: 'grid',
	placeItems: 'center',
	color: colors.textSecondary,
	borderRadius: radius.badge,
	opacity: 1,
	overflow: 'hidden',
	transition: `opacity ${DURATION_LEAD} ease, width ${DURATION_EXPAND} ${EASING}`,
	'&:focus-visible': {
		outline: `${focusVisible.outline} ${colors.brand}`,
		outlineOffset: focusVisible.outlineOffset,
	},
})

export const QtyExpandShell = styled(Box)({
	flexShrink: 0,
	overflow: 'hidden',
	width: PILL_COLLAPSED,
	height: PILL_COLLAPSED,
	borderRadius: radius.badge,
	boxShadow: `inset 0 0 0 1px ${colors.border}`,
	backgroundColor: colors.surfaceWhite,
	transition: `width ${DURATION_EXPAND} ${EASING}, box-shadow ${DURATION_EXPAND} ${EASING}, border-radius ${DURATION_EXPAND} ${EASING}`,
})

export const QtyExpandTrack = styled(Box)({
	display: 'flex',
	flexDirection: 'row',
	width: PILL_EXPANDED,
	height: PILL_COLLAPSED,
	transform: `translate3d(-${PILL_COLLAPSED}px, 0, 0)`,
	transition: `transform ${DURATION_EXPAND} ${EASING}`,
})

export const QtyExpandLead = styled(Box)({
	position: 'relative',
	flexShrink: 0,
	width: PILL_COLLAPSED,
	height: PILL_COLLAPSED,
})

export const QtyExpandBox = styled(Box)({
	flexShrink: 0,
	width: PILL_COLLAPSED,
	height: PILL_COLLAPSED,
	display: 'grid',
	placeItems: 'center',
	fontSize: typography.fontSizeMd,
	fontWeight: typography.fontWeightRegular,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	color: colors.textPrimary,
	userSelect: 'none',
	boxShadow: 'none',
	borderRadius: 0,
	transition: `box-shadow ${DURATION_EXPAND} ${EASING}, border-radius ${DURATION_EXPAND} ${EASING}`,
})

export const ModifierToggleBtn = styled(BaseButton)({
	display: 'inline',
	fontSize: typography.fontSizeSm,
	fontWeight: typography.fontWeightSemiBold,
	lineHeight: typography.lineHeightSm,
	letterSpacing: typography.letterSpacingWidest,
	color: colors.textPrimary,
	textAlign: 'left',
	'&:focus-visible': {
		outline: `${focusVisible.outline} ${colors.brand}`,
		outlineOffset: focusVisible.outlineOffset,
	},
})

export const QtyExpandBtn = styled(BaseButton)({
	flexShrink: 0,
	width: PILL_COLLAPSED,
	height: PILL_COLLAPSED,
	display: 'grid',
	placeItems: 'center',
	color: colors.textSecondary,
	borderRadius: radius.badge,
	'&:hover': {
		backgroundColor: colors.overlayLight,
	},
	'&:focus-visible': {
		outline: `${focusVisible.outline} ${colors.brand}`,
		outlineOffset: focusVisible.outlineOffset,
	},
})
